import React from 'react';

import { css } from '@emotion/react';
import { DollarInputFormatter, MICRO, SocialSecurityInputFormatter } from '@x1/util-format';
import currency from 'currency.js';

import IncomeModal from './IncomeModal';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import IconButton from 'src/components/creditApplication/IconButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { useGlobalStore } from 'src/state/global';

const IncomePage = () => {
  const { nextPage } = React.useContext(NavigationContext);
  const storeSsn = useGlobalStore((s) => s.personalInformation.ssn);
  const ssnEditable = !storeSsn;
  const [income, setIncome] = React.useState<string>('');
  const [ssn, setSsn] = React.useState<string>('');
  const [showIncomeDialog, setShowIncomeDialog] = React.useState<boolean>(false);
  const subtitle = ssnEditable ? (
    <span>
      We will use this to “soft” pull your credit report.
      <br />
      This will not affect your score.
    </span>
  ) : (
    'Please make sure your information is up to date.'
  );

  const buttonState = React.useMemo(() => {
    const ssnOk = !ssnEditable || SocialSecurityInputFormatter.deserialize(ssn).length === 9;
    const incomeOk = !!income && DollarInputFormatter.deserialize(income) !== '0';
    return ssnOk && incomeOk ? 'default' : 'disabled';
  }, [ssnEditable, ssn, income]);

  const onChangeSsn = React.useCallback(({ value }: { value: string }) => {
    const formatted = SocialSecurityInputFormatter.serialize(value);
    setSsn(formatted);
  }, []);

  const onChangeIncome = React.useCallback(({ value }: { value: string }) => {
    const formatted = DollarInputFormatter.serialize(value);
    if (DollarInputFormatter.deserialize(formatted) !== '0') {
      setIncome(formatted);
    } else {
      setIncome('');
    }
  }, []);

  const onShowIncomeDialog = React.useCallback(() => {
    setShowIncomeDialog(true);
  }, []);

  const onDismissIncomeDialog = React.useCallback(() => {
    setShowIncomeDialog(false);
  }, []);

  const onContinue = React.useCallback(async () => {
    await nextPage();
    useGlobalStore.setState((s) => ({
      personalInformation: {
        ...s.personalInformation,
        ssn: ssnEditable ? ssn : s.personalInformation.ssn,
        incomeMicro: currency(income).value * MICRO,
      },
    }));
  }, [ssnEditable, ssn, income]);

  return (
    <CreditApplicationPageLayout title='Your information' subtitle={subtitle}>
      <div css={root}>
        {ssnEditable && (
          <TextInput placeholder='Social Security Number' value={ssn} onChange={onChangeSsn} inputMode='numeric' />
        )}
        <TextInput
          placeholder='Annual income'
          value={income}
          onChange={onChangeIncome}
          inputMode='numeric'
          endControl={
            // TODO (szhang): Figure out why HoverState has differen't base background color
            <IconButton type='button' icon='Info16' onClick={onShowIncomeDialog} aria-label='Show income details' />
          }
        />
        <Button label='Continue' state={buttonState} width='full' type='submit' onClick={onContinue} />
      </div>
      {showIncomeDialog && <IncomeModal onDismiss={onDismissIncomeDialog} />}
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export default IncomePage;
